import React, { Component } from "react";
import { Form, Row, Col, Container, Card } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import * as Cookies from "es-cookie";
import { withRouter } from "react-router-dom";
import { withApollo } from "react-apollo";
import compose from "lodash.flowright";
import gql from "graphql-tag";
import {
  GET_WHATSAPP_TAGS,
  CREATE_WHATSAPP_CONTACT
} from "../../utils/whatsappHelper";
import AssignTestModal from "./AssignTestModal";
import AdmissionBreadcrumb from "./AdmissionBreadcrumb";
import "./_admissionForms.scss";

const CHECK_MOBILE = gql`
  query ($student_mobile: String!, $enquiry_form_id: Int!, $institute_id: Int) {
    checkEnquiryMobile(student_mobile: $student_mobile, enquiry_form_id: $enquiry_form_id, institute_id: $institute_id)
  }
`;

const SUBMIT_ENQUIRY_FORM = gql`
  mutation ($input: SubmitEnquiryFormInput!) {
    submitEnquiryForm(input: $input)
  }
`;

const GET_STREAMS = gql`
  query {
    getStreams {
      id
      stream
      classes
    }
  }
`;

// Classes that require a stream selection (e.g. Intermediate 1st/2nd year)
const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));

const ASSIGN_ADMISSION_TEST_AUTO = gql`
  mutation ($enquiry_form_master_id: Int!, $institute_id: Int!, $branch_id: String!, $inserted_by: Int) {
    assignAdmissionTestAuto(
      enquiry_form_master_id: $enquiry_form_master_id,
      institute_id: $institute_id,
      branch_id: $branch_id,
      inserted_by: $inserted_by
    ) {
      id
      username
      password
    }
  }
`;

class FillAdmissionFormPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // mandatory fields
      student_name: "",
      student_mobile: "",
      student_email: "",
      gender: "",
      dob: "",
      enquiry_course_id: "",
      stream_id: "",
      courseOptions: [],
      streams: [],

      // dynamic fields
      dynamicFields: [],
      dynamicFieldValues: {},

      // ui state
      loader: false,
      errors: {},
      submitError: "",

      // after success
      showSuccess: false,
      newStudentId: null,

      // Assign test modal integration
      showAssignModal: false,
      autoAssigning: false,
      autoAssignError: "",

      // CRM Integration (WhatsApp)
      whatsAppTags: [],
      selectedTagId: "",
      isContactCreating: false,
      contactCreatedStatus: null, // 'success' | 'error' | null

      // Duplicate Check
      isDuplicateMobile: false,
      siblingUndertaking: false,
      isCheckingMobile: false,

      academicFieldIds: {},
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0); // Scroll to top when form opens
    this.initFormFields();
    this.fetchWhatsAppTags();
    this.fetchStreams();
  }

  fetchStreams = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_STREAMS,
        fetchPolicy: "network-only"
      });
      this.setState({ streams: result.data.getStreams || [] });
    } catch (err) {
      console.error("Error fetching streams:", err);
    }
  };

  fetchWhatsAppTags = async () => {
    const employeeId = parseInt(Cookies.get("id"));
    if (!employeeId) return;

    try {
      const result = await this.props.client.query({
        query: GET_WHATSAPP_TAGS,
        variables: { employee_id: employeeId },
        fetchPolicy: 'network-only'
      });

      if (result.data?.getWhatsAppTags?.success) {
        this.setState({ whatsAppTags: result.data.getWhatsAppTags.data || [] });
      }
    } catch (err) {
      console.error("Error fetching WhatsApp tags:", err);
    }
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.getEnquiryFormFields !== prevProps.getEnquiryFormFields &&
      !this.props.getEnquiryFormFields.loading
    ) {
      this.initFormFields();
    }
  }

  // Resolve a field's master key (or null for custom fields). Uses field_key first,
  // falling back to the label so renamed basic fields are still recognized.
  resolveBaseKey = (f) => {
    const MASTER_KEYS = ["student_name", "student_mobile", "student_email", "dob", "gender", "enquiry_course_id", "stream_id"];
    if (f.field_key && MASTER_KEYS.includes(f.field_key)) return f.field_key;
    const label = (f.field_name || "").toLowerCase().trim().replace(/_/g, " ");
    if (["name", "full name", "student name"].includes(label)) return "student_name";
    if (["mobile", "mobile number", "student mobile", "primary contact number", "contact number", "phone number"].includes(label)) return "student_mobile";
    if (label.includes("email")) return "student_email";
    if (["date of birth", "dob"].includes(label)) return "dob";
    if (["gender", "sex"].includes(label)) return "gender";
    if (["enquiry class", "course name", "course"].includes(label)) return "enquiry_course_id";
    if (label === "stream") return "stream_id";
    return null;
  };

  initFormFields = () => {
    const fields =
      this.props.getEnquiryFormFields &&
        this.props.getEnquiryFormFields.getEnquiryFormFields
        ? this.props.getEnquiryFormFields.getEnquiryFormFields
        : [];

    if (fields.length > 0) {
      const academicKws = ["school", "cgpa", "percentage", "marks", "ssc gpa", "state", "district", "city", "pincode", "zipcode", "zip code"];

      // Dynamic labels/required for the master fields, keyed by master key — so the
      // basic fields show their saved (possibly edited) names.
      const baseLabels = {};
      const baseRequired = {};
      const baseFields = [];
      let courseOptions = this.state.courseOptions;

      const dynamic = fields.filter((f) => {
        const key = this.resolveBaseKey(f);
        if (key) {
          // Email is not shown by default on the form.
          if (key === "student_email") return false;
          baseLabels[key] = f.field_name;
          baseRequired[key] = !!f.is_required;
          if (key === "enquiry_course_id") courseOptions = f.options || [];
          baseFields.push({ ...f, _baseKey: key });
          return false; // master fields are rendered separately (in order)
        }
        const label = (f.field_name || "").toLowerCase().trim().replace(/_/g, " ");
        const isAcademic = academicKws.some((kw) => label.includes(kw));
        return !isAcademic;
      })
        // Show custom fields in their saved order_no order.
        .sort((a, b) => (a.order_no || 0) - (b.order_no || 0));

      const byOrder = (a, b) => (a.order_no || 0) - (b.order_no || 0);
      baseFields.sort(byOrder);

      this.setState({
        dynamicFields: dynamic,
        baseFields,
        baseLabels,
        baseRequired,
        courseOptions,
      });
    }
  };



  handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    // Name field: Accept only letters, spaces, and hyphens
    if (name === "student_name") {
      filteredValue = value.replace(/[^a-zA-Z\s\-']/g, "");
    }

    // Mobile field: Accept only digits
    if (name === "student_mobile") {
      filteredValue = value.replace(/[^0-9]/g, "");
    }

    this.setState({
      [name]: filteredValue,
      errors: { ...this.state.errors, [name]: "" },
      // Changing the course invalidates any previously chosen stream
      ...(name === "enquiry_course_id" ? { stream_id: "" } : {}),
    }, () => {
      // Reset duplicate state when mobile changes
      if (name === "student_mobile") {
        this.setState({ isDuplicateMobile: false, siblingUndertaking: false });
      }
    });
  };

  handleMobileBlur = async () => {
    const { student_mobile } = this.state;
    if (student_mobile && student_mobile.length === 10) {
      this.setState({ isCheckingMobile: true });
      try {
        const { data } = await this.props.client.query({
          query: CHECK_MOBILE,
          variables: {
            student_mobile: student_mobile,
            enquiry_form_id: parseInt(this.props.formId) || 0,
            institute_id: parseInt(Cookies.get("institutionid")) || 0
          },
          fetchPolicy: "network-only",
        });

        if (data && data.checkEnquiryMobile) {
          this.setState({ isDuplicateMobile: true });
        } else {
          this.setState({ isDuplicateMobile: false, siblingUndertaking: false });
        }
      } catch (err) {
        console.error("Error checking mobile duplicate:", err);
      } finally {
        this.setState({ isCheckingMobile: false });
      }
    }
  };

  // -- Secure: Data Leak Removed --
  // We no longer call getEnquirySubmissions here. 
  // Validation happens on submit to prevent scraping.
  checkDuplicateMobile = () => { };

  // ── Centralized type resolver ─────────────────────────────────
  // Prioritizes field_type_id (numeric) over field_type (string label)
  resolveTypeId = (field) => {
    // 1) Try field_type_id first (numeric from DB)
    if (field.field_type_id != null && !isNaN(parseInt(field.field_type_id))) {
      return parseInt(field.field_type_id);
    }
    // 2) Fallback: parse field_type as number or string label
    const raw = (field.field_type || "").toString().trim().toLowerCase();
    if (raw === "1" || raw === "text") return 1;
    if (raw === "2" || raw === "textarea") return 2;
    if (raw === "3" || raw === "number") return 3;
    if (raw === "4" || raw === "dropdown" || raw === "select") return 4;
    if (raw === "5" || raw === "radio" || raw === "radiobutton") return 5;
    if (raw === "6" || raw === "date") return 6;
    if (raw === "7" || raw === "checkbox") return 7;
    return 1; // default to Text
  };

  handleDynamicChange = (fieldId, value) => {
    const field = this.state.dynamicFields.find((f) => f.id === fieldId);
    let filteredValue = value;

    if (field) {
      const typeId = this.resolveTypeId(field);

      // Number field (ID: 3): Accept only digits and decimal point
      if (typeId === 3) {
        filteredValue = value.replace(/[^0-9.]/g, "");
        // Prevent multiple decimal points
        if ((filteredValue.match(/\./g) || []).length > 1) {
          filteredValue = filteredValue.replace(/\.(?=.*\.)/, "");
        }
      }

      // Text field (ID: 1): Accept only letters and spaces for name-related fields
      if (typeId === 1) {
        const fieldNameLower = (field.field_name || "").toLowerCase();
        if (
          fieldNameLower.includes("name") ||
          (fieldNameLower.includes("student") &&
            fieldNameLower.includes("name"))
        ) {
          filteredValue = value.replace(/[^a-zA-Z\s\-']/g, "");
        }
      }

      // Enforce the configured max length (value_length) for text-like inputs.
      const maxLen = parseInt(field.value_length);
      if (!isNaN(maxLen) && maxLen > 0 && typeof filteredValue === "string" && filteredValue.length > maxLen) {
        filteredValue = filteredValue.slice(0, maxLen);
      }
    }

    this.setState((prev) => ({
      dynamicFieldValues: {
        ...prev.dynamicFieldValues,
        [fieldId]: filteredValue,
      },
      errors: { ...prev.errors, [`field_${fieldId}`]: "" },
    }));
  };

  validate = () => {
    const {
      student_name,
      student_mobile,
      student_email,
      gender,
      dob,
      dynamicFields,
      dynamicFieldValues,
    } = this.state;
    const errors = {};

    if (!student_name.trim()) errors.student_name = "Name is required";

    if (!student_mobile.trim()) errors.student_mobile = "Mobile is required";
    else if (!/^\d{10}$/.test(student_mobile.trim()))
      errors.student_mobile = "Enter valid 10 digit mobile";

    if (!this.state.enquiry_course_id)
      errors.enquiry_course_id = "Enquiry Class is required";

    // Stream is required when the selected course maps to a stream class (15/16)
    const selectedCourseForStream = (this.state.courseOptions || []).find(
      c => String(c.value) === String(this.state.enquiry_course_id)
    );
    if (selectedCourseForStream && isStreamClass(selectedCourseForStream.class_id) && !this.state.stream_id) {
      errors.stream_id = "Stream is required";
    }

    /* Validation for DOB and Gender removed as they are no longer mandatory. */

    if (student_email.trim() && !/\S+@\S+\.\S+/.test(student_email.trim()))
      errors.student_email = "Enter a valid email address";



    // validate required dynamic fields with field-type-specific validation
    dynamicFields.forEach((field) => {
      const value = dynamicFieldValues[field.id];
      const typeId = this.resolveTypeId(field);

      // Check if required
      if (field.is_required && !value) {
        errors[`field_${field.id}`] = `${field.field_name} is required`;
        return;
      }

      if (value) {
        // Validate field type
        if (typeId === 3) { // Number
          if (isNaN(parseFloat(value))) {
            errors[`field_${field.id}`] =
              `${field.field_name} must be a valid number`;
          }
        } else if (typeId === 6) { // Date
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            errors[`field_${field.id}`] =
              `${field.field_name} must be a valid date`;
          }
        }
      }
    });



    return errors;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors }, () => {
        // Find the first element with an error (either the input or the error message)
        const firstError = document.querySelector('.is-invalid, .text-danger');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return;
    }

    const {
      student_name,
      student_mobile,
      student_email,
      gender,
      dob,
      dynamicFields,
      dynamicFieldValues,
    } = this.state;

    const { formId, stateData } = this.props;
    const branch_id = String(stateData.branch_id);

    // Safety: ensure branch_id is a valid number before submission
    if (!branch_id || branch_id === "undefined" || branch_id === "null" || isNaN(parseInt(branch_id))) {
      this.setState({ submitError: "Branch information is missing. Please go back to the forms list and select a form." });
      return;
    }

    const institute_id = parseInt(Cookies.get("institutionid"));
    const userId = Cookies.get("user_id") || Cookies.get("id");
    const inserted_by = userId ? parseInt(userId) : 0;

    const remainingFormData = dynamicFields.map((field) => ({
      enquiry_field_id: field.id,
      value: dynamicFieldValues[field.id] || "",
    }));



    const selectedCourseId = parseInt(this.state.enquiry_course_id) || 0;

    if (this.state.isDuplicateMobile && !this.state.siblingUndertaking) {
      this.setState({ submitError: "Please confirm the Sibling Undertaking to proceed." });
      return;
    }

    this.setState({ loader: true, submitError: "" });

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const source = queryParams.get("source") || "Direct";

      const selectedCourseForSubmit = (this.state.courseOptions || []).find(
        c => String(c.value) === String(this.state.enquiry_course_id)
      );
      const courseNeedsStream = selectedCourseForSubmit && isStreamClass(selectedCourseForSubmit.class_id);

      const result = await this.props.client.mutate({
        mutation: SUBMIT_ENQUIRY_FORM,
        variables: {
          input: {
            enquiry_form_id: parseInt(formId),
            institute_id,
            branch_id: branch_id,
            student_name: student_name.trim(),
            student_mobile: student_mobile.trim(),
            student_email: student_email.trim(),
            gender,
            dob,
            enquiry_course_id: selectedCourseId,
            stream_id: courseNeedsStream && this.state.stream_id ? parseInt(this.state.stream_id) : null,
            inserted_by,
            is_sibling: this.state.siblingUndertaking, // Pass the sibling flag
            remainingFormData,
            source_name: source
          },
        },
      });

      // submitEnquiryForm returns the new enquiry_form_master id
      const newStudentId = result.data.submitEnquiryForm;

      this.setState({
        loader: false,
        showSuccess: true,
        newStudentId,
      });

      // Trigger WhatsApp Contact Creation in background
      this.createCRMContact(student_name, student_mobile, student_email, branch_id);

    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("already registered") || msg.includes("Duplicate") || msg.includes("already exists")) {
        this.setState({
          loader: false,
          isDuplicateMobile: true,
          submitError: "This mobile is already registered. If this is a sibling, check the box below and try again."
        });
      } else {
        this.setState({
          loader: false,
          submitError: msg.replace("GraphQL error: ", ""),
        });
      }
    }
  };
  createCRMContact = async (name, mobile, email, branch_id) => {
    const employeeId = parseInt(Cookies.get("user_id") || Cookies.get("id"));
    const { selectedTagId } = this.state;

    if (!employeeId || !mobile) return;

    this.setState({ isContactCreating: true });
    try {
      const result = await this.props.client.mutate({
        mutation: CREATE_WHATSAPP_CONTACT,
        variables: {
          employee_id: employeeId,
          name: name.trim(),
          mobile: mobile.trim(),
          tag_id: selectedTagId || null,
          email: email ? email.trim() : null,
          branch_id: branch_id ? parseInt(branch_id, 10) : null
        }
      });

      if (result.data?.createWhatsAppContact?.success) {
        this.setState({ contactCreatedStatus: 'success' });
      } else {
        this.setState({ contactCreatedStatus: 'error' });
      }
    } catch (err) {
      console.error("CRM Contact creation failed:", err);
      this.setState({ contactCreatedStatus: 'error' });
    } finally {
      this.setState({ isContactCreating: false });
    }
  };

  handleAssignTest = () => {
    this.setState({ showAssignModal: true });
  };

  handleSkip = () => {
    this.handleCancel();
  };

  handleAssignModalClose = () => {
    this.handleCancel();
  };

  handleCancel = () => {
    if (typeof this.props.onClose === "function") {
      this.props.onClose();
    } else {
      this.props.history.push("/admission-forms");
    }
  };

  handleReset = () => {
    this.setState({
      student_name: "",
      student_mobile: "",
      student_email: "",
      gender: "",
      dob: "",
      enquiry_course_id: "",
      stream_id: "",
      dynamicFieldValues: {}, // Clear dynamic fields too
      errors: {},
      submitError: "",
      showSuccess: false, // Reset success view
      newStudentId: null,
      isDuplicateMobile: false,
      siblingUndertaking: false,
      isCheckingMobile: false,
      contactCreatedStatus: null,

    });
  };

  // Render a master/basic field by its key. Used so basic fields display in
  // order_no order with their saved (editable) names.
  renderBaseField = (f) => {
    const { student_name, student_mobile, student_email, gender, dob, enquiry_course_id, stream_id, courseOptions, streams, errors } = this.state;
    const key = f._baseKey;
    const label = f.field_name;
    const required = !!f.is_required;
    const star = required ? <span className="text-danger">*</span> : null;

    switch (key) {
      case "student_name":
        return (
          <Form.Group className="mb-4">
            <Form.Label className="af-field-label">{label} {star}</Form.Label>
            <Form.Control type="text" name="student_name" value={student_name} onChange={this.handleChange}
              maxLength={f.value_length || undefined}
              placeholder="e.g. John Doe" className={`form-control ${errors.student_name ? "is-invalid" : ""}`} />
            {errors.student_name && <small className="text-danger mt-1">{errors.student_name}</small>}
          </Form.Group>
        );
      case "student_mobile":
        return (
          <Form.Group className="mb-4">
            <Form.Label className="af-field-label">{label} {star}</Form.Label>
            <Form.Control type="tel" name="student_mobile" value={student_mobile} onChange={this.handleChange}
              onBlur={this.handleMobileBlur} placeholder="10 digit number" maxLength={f.value_length || 10}
              className={`form-control ${errors.student_mobile ? "is-invalid" : ""}`} />
            {errors.student_mobile && <small className="text-danger mt-1">{errors.student_mobile}</small>}
          </Form.Group>
        );
      case "student_email":
        return (
          <Form.Group className="mb-4">
            <Form.Label className="af-field-label">{label} {star}</Form.Label>
            <Form.Control type="email" name="student_email" value={student_email} onChange={this.handleChange}
              maxLength={f.value_length || undefined}
              placeholder="name@example.com" className={`form-control ${errors.student_email ? "is-invalid" : ""}`} />
            {errors.student_email && <small className="text-danger mt-1">{errors.student_email}</small>}
          </Form.Group>
        );
      case "dob":
        return (
          <Form.Group className="mb-4">
            <Form.Label className="af-field-label">{label} {star}</Form.Label>
            <Form.Control type="date" name="dob" value={dob} onChange={this.handleChange}
              className={`form-control ${errors.dob ? "is-invalid" : ""}`} />
            {errors.dob && <small className="text-danger mt-1">{errors.dob}</small>}
          </Form.Group>
        );
      case "enquiry_course_id": {
        // Stream is rendered right after the course (independent of whether a
        // "Stream" field exists in the saved form) so it always shows for 15/16.
        const selectedCourse = (courseOptions || []).find(c => String(c.value) === String(enquiry_course_id));
        const showStream = selectedCourse && isStreamClass(selectedCourse.class_id);
        const streamOptions = showStream
          ? (streams || []).filter(st => (st.classes || "").split(",").map(c => c.trim()).includes(String(selectedCourse.class_id)))
          : [];
        return (
          <React.Fragment>
            <Form.Group className="mb-4">
              <Form.Label className="af-field-label">{label} {star}</Form.Label>
              <Form.Control as="select" name="enquiry_course_id" value={enquiry_course_id} onChange={this.handleChange}
                className={`form-control ${errors.enquiry_course_id ? "is-invalid" : ""}`}>
                <option value="">Select {label}</option>
                {(courseOptions || []).map((course) => (
                  <option key={course.value} value={course.value}>{course.label}</option>
                ))}
              </Form.Control>
              {errors.enquiry_course_id && <small className="text-danger mt-1">{errors.enquiry_course_id}</small>}
            </Form.Group>
            {showStream && (
              <Form.Group className="mb-4">
                <Form.Label className="af-field-label">Stream <span className="text-danger">*</span></Form.Label>
                <Form.Control as="select" name="stream_id" value={stream_id} onChange={this.handleChange}
                  className={`form-control ${errors.stream_id ? "is-invalid" : ""}`}>
                  <option value="">Select Stream</option>
                  {streamOptions.map(st => (
                    <option key={st.id} value={st.id}>{st.stream}</option>
                  ))}
                </Form.Control>
                {errors.stream_id && <small className="text-danger mt-1">{errors.stream_id}</small>}
              </Form.Group>
            )}
          </React.Fragment>
        );
      }
      // Stream is rendered with the course field above; suppress the standalone
      // master field so it isn't duplicated when present in getEnquiryFormFields.
      case "stream_id":
        return null;
      case "gender": {
        const genderOpts = (f.options && (Array.isArray(f.options) ? f.options.length : true))
          ? (Array.isArray(f.options)
              ? f.options.map((o) => (typeof o === "string" ? { label: o, value: o } : o))
              : f.options.toString().split(",").map((o) => ({ label: o.trim(), value: o.trim() })).filter((o) => o.label))
          : [{ label: "Boy", value: "Boy" }, { label: "Girl", value: "Girl" }];
        return (
          <Form.Group className="mb-4">
            <Form.Label className="af-field-label">{label} {star}</Form.Label>
            <Form.Control as="select" name="gender" value={gender} onChange={this.handleChange}
              className={`form-control ${errors.gender ? "is-invalid" : ""}`}>
              <option value="">Select {label}</option>
              {genderOpts.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
            </Form.Control>
            {errors.gender && <small className="text-danger mt-1">{errors.gender}</small>}
          </Form.Group>
        );
      }
      default:
        return null;
    }
  };

  renderDynamicField = (field) => {
    const { dynamicFieldValues, errors } = this.state;
    const value = dynamicFieldValues[field.id] || "";
    const errKey = `field_${field.id}`;

    // Use centralized resolver: prioritizes field_type_id over field_type
    let typeId = this.resolveTypeId(field);

    // ── FORCED PATCH: Ensure Course Name is always a dropdown
    const fieldNameLower = (field.field_name || "").toLowerCase();
    if (fieldNameLower.includes("course") || fieldNameLower.includes("course name") || fieldNameLower.includes("enquiry class")) {
      typeId = 4;
    }

    const getOptions = (options) => {
      if (!options) return [];
      if (Array.isArray(options)) {
        return options.map(opt => typeof opt === 'string' ? { label: opt, value: opt } : opt);
      }
      return options.toString().split(',').map(o => ({ label: o.trim(), value: o.trim() })).filter(o => o.label);
    };

    if (typeId === 4) { // Dropdown
      const options = getOptions(field.options);
      return (
        <Form.Group key={field.id} className="mb-4">
          <Form.Label className="af-field-label">
            {field.field_name} {field.is_required && <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            as="select"
            value={value}
            onChange={(e) => this.handleDynamicChange(field.id, e.target.value)}
            className={`form-control ${errors[errKey] ? "is-invalid" : ""}`}
          >
            <option value="">Select Enquiry Class</option>
            {options.map((opt, i) => (
              <option key={i} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Control>
          {errors[errKey] && <small className="text-danger mt-1 d-block">{errors[errKey]}</small>}
        </Form.Group>
      );
    }

    if (typeId === 5) { // Radio
      const options = getOptions(field.options);
      return (
        <Form.Group key={field.id} className="mb-4">
          <Form.Label className="af-field-label">
            {field.field_name} {field.is_required && <span className="text-danger">*</span>}
          </Form.Label>
          <div className="af-gender-selector mt-1">
            {options.map((opt, i) => (
              <label key={i} className="af-gender-option mb-0">
                <input
                  type="radio"
                  name={`dynamic_${field.id}`}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={() => this.handleDynamicChange(field.id, opt.value)}
                />
                <span className="af-gender-pill">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
          {errors[errKey] && <small className="text-danger mt-2 d-block">{errors[errKey]}</small>}
        </Form.Group>
      );
    }

    if (typeId === 7) { // Checkbox
      const options = getOptions(field.options);
      return (
        <Form.Group key={field.id} className="mb-4">
          <Form.Label className="af-field-label">
            {field.field_name} {field.is_required && <span className="text-danger">*</span>}
          </Form.Label>
          <div className="d-flex flex-row flex-wrap gap-4 mt-1">
            {options.map((opt, i) => {
              const currentValues = Array.isArray(value) ? value : (value ? value.split(',') : []);
              return (
                <label key={i} className="mb-0 custom-control custom-checkbox d-flex align-items-center" style={{ gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={currentValues.includes(opt.value)}
                    onChange={(e) => {
                      let newValues = [...currentValues];
                      if (e.target.checked) newValues.push(opt.value);
                      else newValues = newValues.filter(v => v !== opt.value);
                      this.handleDynamicChange(field.id, newValues.join(','));
                    }}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500", color: "#64748b" }}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
          {errors[errKey] && <small className="text-danger mt-2 d-block">{errors[errKey]}</small>}
        </Form.Group>
      );
    }

    const inputType =
      typeId === 3
        ? "number"
        : typeId === 6
          ? "date"
          : "text";

    // For textarea fields (ID: 2), render as textarea instead of input
    if (typeId === 2) {
      return (
        <Form.Group key={field.id} className="mb-3">
          <Form.Label className="af-field-label">
            {field.field_name}
            {field.is_required && <span className="text-danger ml-1">*</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={value}
            maxLength={field.value_length || undefined}
            onChange={(e) => this.handleDynamicChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_name}`}
            className={`form-control ${errors[errKey] ? "is-invalid" : ""}`}
            style={{ borderColor: errors[errKey] ? "#dc3545" : undefined }}
          />
          {errors[errKey] && (
            <small className="text-danger mt-1">{errors[errKey]}</small>
          )}
        </Form.Group>
      );
    }

    return (
      <Form.Group key={field.id} className="mb-4">
        <Form.Label className="af-field-label">
          {field.field_name} {field.is_required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Control
          type={inputType}
          value={value}
          maxLength={field.value_length || undefined}
          onChange={(e) => this.handleDynamicChange(field.id, e.target.value)}
          placeholder={`Enter ${field.field_name.toLowerCase()}`}
          className={`form-control ${errors[errKey] ? "is-invalid" : ""}`}
        />
        {errors[errKey] && <small className="text-danger mt-1 d-block">{errors[errKey]}</small>}
      </Form.Group>
    );
  };

  render() {
    const {
      student_name,
      student_mobile,
      student_email,
      gender,
      dob,
      dynamicFields,
      baseFields = [],
      errors,
      loader,
      submitError,
      showSuccess,
      newStudentId,
      baseLabels = {},
      baseRequired = {},
    } = this.state;

    const reqStar = (key, dflt) => {
      const r = baseRequired[key];
      return (r === undefined ? dflt : r) ? <span className="text-danger">*</span> : null;
    };

    const { stateData } = this.props;
    const formName = stateData.form_name || "Enquiry Form";

    // If successfully submitted, render premium success state
    if (showSuccess) {
      const { branch_id } = stateData;
      const studentForModal = {
        id: newStudentId,
        student_name: student_name,
        student_mobile: student_mobile,
        branch_id: branch_id
      };

      return (
        <Container fluid className="px-3 pb-5 h-100 d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
          <Card className="af-premium-success-card shadow-lg border-0 w-100" style={{ maxWidth: '600px', borderRadius: '16px', overflow: 'hidden' }}>
            <div className="text-center py-5 bg-success text-white">
              <div className="af-success-icon-check mb-3">
                <i className="fas fa-check-circle fa-4x" />
              </div>
              <h2 className="font-weight-bold mb-0">Registration successful.</h2>
              {/* <p className="opacity-75 mb-0">Check your WhatsApp for a confirmation message.</p> */}
            </div>

            <Card.Body className="p-4">
              {/* Data Summary */}
              <div className="af-success-data-summary p-3 rounded mb-4" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted small font-weight-bold">STUDENT NAME</Col>
                  <Col xs={7} className="text-dark font-weight-bold">{student_name}</Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={5} className="text-muted small font-weight-bold">PRIMARY CONTACT NUMBER</Col>
                  <Col xs={7} className="text-dark">{student_mobile}</Col>
                </Row>
                {/* <Row>
                  <Col xs={7}>
                    {this.state.isContactCreating ? (
                      <span className="text-primary italic small">
                        <Spinner animation="border" size="sm" className="mr-1" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                      </span>
                    ) : this.state.contactCreatedStatus === 'success' ? (
                      <span className="text-success small font-weight-bold">
                      </span>
                    ) : this.state.contactCreatedStatus === 'error' ? (
                      <span className="text-danger small">
                      </span>
                    ) : (
                      <span className="text-muted small">
                      </span>
                    )}
                  </Col>
                </Row> */}
              </div>

              {/* Action Grid */}
              <div className="af-success-actions-grid d-flex flex-column gap-3">
                {/* <Button
                  variant="primary"
                  className="af-premium-btn-v2 py-3"
                  onClick={this.handleAssignTest}
                >
                  <i className="fas fa-tasks mr-2" /> Assign Admission Test & Schedule
                </Button> */}

                <div className="d-flex flex-column flex-sm-row gap-3 mt-1">
                  {/* <Button
                    variant="outline-primary"
                    className="w-100 py-2 mb-2 mb-sm-0"
                    onClick={() => this.props.history.push('/communications-center', { student_mobile })}
                  >
                    <i className="fab fa-whatsapp mr-2" /> Send Message
                  </Button> */}
                  <Button
                    variant="light"
                    className="w-100 py-2 text-muted"
                    onClick={this.handleCancel}
                  >
                    Return to Forms List
                  </Button>
                  <Button
                    variant="success"
                    className="w-100 py-2 font-weight-bold"
                    onClick={this.handleReset}
                  >
                    <i className="fas fa-plus mr-2" />
                    Fill Another Form
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          <AssignTestModal
            show={this.state.showAssignModal}
            onHide={() => this.setState({ showAssignModal: false })}
            student={studentForModal}
          />
        </Container>
      );
    }

    return (
      <Container fluid className="px-0 pb-5">
        <div className="af-fill-form-container">
          {/* Breadcrumb + page header only in standalone view (hidden inside the
              Add Enquiry modal, which has its own header) */}
          {!this.props.onClose && (
            <>
              <AdmissionBreadcrumb
                items={[
                  { label: 'Enquiries', path: '/admissions-dashboard' },
                  { label: 'Forms', path: '/admission-forms' },
                  { label: formName }
                ]}
              />
              <div className="af-page-header-minimal">
                <h4 className="af-page-title">
                  <i className="fas fa-file-signature" /> {formName}
                </h4>
                <p className="af-page-subtitle">
                  Complete student registration by filling in the details below. All marked (*) fields are mandatory.
                </p>
              </div>
            </>
          )}

          <div className="d-flex flex-column gap-4">
            {/* Basic Information Section */}
            <div className="af-shadcn-card p-4 shadow-sm">
              {submitError && (
                <div className="alert alert-danger d-flex align-items-center mb-4" style={{ fontSize: "14px", borderRadius: "6px" }}>
                  <i className="fas fa-exclamation-circle mr-2" /> {submitError}
                </div>
              )}

              <div className="af-form-section-heading">
                <i className="fas fa-user-circle" /> Basic Information
              </div>

              <Row>
                {/* Master fields rendered in saved order_no order */}
                {baseFields.map((f) => (
                  <Col lg={6} md={6} sm={12} key={f.id}>
                    {this.renderBaseField(f)}
                  </Col>
                ))}

                {/* Custom fields — same form, in order_no order */}
                {dynamicFields.map((field) => (
                  <Col lg={6} md={6} sm={12} key={field.id}>
                    {this.renderDynamicField(field)}
                  </Col>
                ))}

                {/* Sibling Checkbox / Duplicate Validation */}
                {this.state.isDuplicateMobile && (
                  <Col md={12}>
                    <div className="af-duplicate-notice p-3 mb-4 rounded border" style={{ backgroundColor: '#fff7ed', borderColor: '#fed7aa' }}>
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-exclamation-triangle text-warning mr-2" style={{ fontSize: '18px' }} />
                        <strong style={{ color: '#9a3412', fontSize: '15px' }}>Duplicate Mobile Number Detected</strong>
                      </div>
                      <p className="mb-3" style={{ fontSize: '14px', color: '#7c2d12' }}>
                        This mobile number is already registered in our system. If this is a different student (sibling), please confirm below.
                      </p>

                      <label className="d-flex align-items-center cursor-pointer mb-2" style={{ gap: '10px' }}>
                        <input
                          type="checkbox"
                          checked={this.state.siblingUndertaking}
                          onChange={(e) => this.setState({ siblingUndertaking: e.target.checked })}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                          Is this a Sibling? I undertake that this is a separate entry for a sibling.
                        </span>
                      </label>

                      {!this.state.siblingUndertaking && (
                        <div className="mt-3 p-2 rounded bg-danger text-white text-center font-weight-bold" style={{ fontSize: '14px' }}>
                          <i className="fas fa-ban mr-2" />
                          Multiple entry detected. Registration terminated.
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            {/* Tag Selection Section commented out as per request */}
            {/* 
            <div className="af-shadcn-card p-4 shadow-sm">
              <div className="af-form-section-heading">
                <i className="fas fa-tags" /> Tag Selection
              </div>
              <Row>
                <Col lg={6} md={12}>
                  <Form.Group className="mb-0">
                    <Form.Label className="af-field-label">Admission Category / Tag <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="select"
                      value={this.state.selectedTagId}
                      onChange={(e) => this.setState({ selectedTagId: e.target.value, errors: { ...this.state.errors, selectedTagId: "" } })}
                      className={`form-control ${errors.selectedTagId ? "is-invalid" : ""}`}
                    >
                      <option value="">-- Select Tag --</option>
                      {this.state.whatsAppTags.map(tag => (
                        <option key={tag.id} value={tag.id}>{tag.tag_name}</option>
                      ))}
                    </Form.Control>
                    {errors.selectedTagId && <small className="text-danger mt-1 d-block">{errors.selectedTagId}</small>}
                    <small className="text-muted mt-2 d-block italic">
                      Please select a category to organize this student and enable follow-up communications.
                    </small>
                  </Form.Group>
                </Col>
              </Row>
            </div>
            */}

            {/* Form Actions */}
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-3 pt-3">
              <button
                type="button"
                className="af-cancel-btn mr-md-3"
                onClick={this.handleCancel}
                disabled={loader}
                style={{ minWidth: "120px" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="af-save-btn"
                onClick={this.handleSubmit}
                disabled={loader}
                style={{ minWidth: "160px" }}
              >
                {loader ? (
                  <><span className="spinner-border spinner-border-sm mr-2" /> Saving...</>
                ) : (
                  <>Submit</>
                )}
              </button>
            </div>
          </div>
        </div>

      </Container>
    );
  }
}

export default compose(
  withRouter,
  withApollo
)(FillAdmissionFormPage);
